import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { env } from "../../../env";
import axiosInstance from "../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

export const internalTxnAccountDetail = createAsyncThunk(
  "internalTxnAccountDetail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/internal/${id}`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
        },
      );
      return response.data;
    } catch (error) {
      console.error("error", error);
      throw error;
    }
  },
);

export const internalTxnRequireApprovalPage = createAsyncThunk(
  "internalTxnRequireApprovalPage",
  async (values) => {
    const { page_no, page_size, category, entityId } = values;
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/internal/actionable`,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          params: {
            page_no: page_no ? page_no : undefined,
            page_size: page_size ? page_size : undefined,
            category: category !== undefined ? category : undefined,
            entity_id: entityId ? entityId : undefined,
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
);

export const internalTxnApproval = createAsyncThunk(
  "internalTxnApproval",
  async (id, { rejectWithValue }) => {
    try {
      const payload = {
        txn_id: [id],
      };

      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/approve`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response.data || "Unexpected error");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

export const internalTxnDenied = createAsyncThunk(
  "internalTxnDenied",
  async ({ id, AccountIdData, flow = "" }, { rejectWithValue }) => {
    try {
      const payload = {
        txn_id: flow === "multiApproval" ? id : [id],
        reason: AccountIdData,
      };

      const response = await axiosInstance.put(
        `${baseUrl}/transactions/internal/reject`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            "X-Api-Key": sessionStorage.getItem("q2p_token"),
          },
          validateStatus: () => true,
        },
      );

      if (response.status === 404) {
        return rejectWithValue(undefined);
      }

      if (response.status < 200 || response.status >= 300) {
        return rejectWithValue(response.data || "Unexpected error");
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error.message);
    }
  },
);

const internalTxnDataSlice = createSlice({
  name: "internalTxnViewData",
  initialState: {
    internalTxnDataLoading: null,
    internalTxnData: null,
    internalTxnDataError: null,

    internalTxnRequireApprovalData: null,
    internalTxnRequireApprovalLoading: null,
    internalTxnRequireApprovalError: null,

    internalTxnApprovalData: null,
    internalTxnApprovalLoading: null,
    internalTxnApprovalError: null,

    internalTxnDeniedData: null,
    internalTxnDeniedLoading: null,
    internalTxnDeniedError: null,
  },

  reducers: {
    updateInternalDetailData: (state, action) => {
      state.internalTxnData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(internalTxnAccountDetail.pending, (state, action) => {
      state.internalTxnDataLoading = true;
    });
    builder.addCase(internalTxnAccountDetail.fulfilled, (state, action) => {
      state.internalTxnDataLoading = false;
      state.internalTxnData = action.payload;
    });
    builder.addCase(internalTxnAccountDetail.rejected, (state, action) => {
      state.internalTxnDataError = true;
    });
    builder.addCase(internalTxnRequireApprovalPage.pending, (state, action) => {
      state.internalTxnRequireApprovalLoading = true;
    });
    builder.addCase(
      internalTxnRequireApprovalPage.fulfilled,
      (state, action) => {
        state.internalTxnRequireApprovalLoading = false;
        state.internalTxnRequireApprovalData = action.payload;
      },
    );
    builder.addCase(
      internalTxnRequireApprovalPage.rejected,
      (state, action) => {
        state.internalTxnRequireApprovalError = true;
        state.internalTxnRequireApprovalData = null;
      },
    );
    builder.addCase(internalTxnApproval.pending, (state, action) => {
      state.internalTxnApprovalLoading = true;
    });
    builder.addCase(internalTxnApproval.fulfilled, (state, action) => {
      state.internalTxnApprovalLoading = false;
      state.internalTxnApprovalData = action.payload;
    });
    builder.addCase(internalTxnApproval.rejected, (state, action) => {
      state.internalTxnApprovalError = true;
      state.internalTxnApprovalData = null;
    });
    builder.addCase(internalTxnDenied.pending, (state, action) => {
      state.internalTxnDeniedLoading = true;
    });
    builder.addCase(internalTxnDenied.fulfilled, (state, action) => {
      state.internalTxnDeniedLoading = false;
      state.internalTxnDeniedData = action.payload;
    });
    builder.addCase(internalTxnDenied.rejected, (state, action) => {
      state.internalTxnDeniedError = true;
      state.internalTxnDeniedData = null;
      state.internalTxnDeniedLoading = false;
      sessionStorage.setItem(
        "selectedDataError",
        JSON.stringify(action.payload?.errors?.base),
      );
    });
  },
});
export const { updateInternalDetailData } = internalTxnDataSlice.actions;

export default internalTxnDataSlice.reducer;
